/**
 * date.js — cross-browser date helpers.
 */

/**
 * Safari-safe date parser.
 *
 * The engine API returns dates in the space-separated "YYYY-MM-DD HH:mm[:ss]"
 * format. Chrome/Firefox parse `new Date("2021-09-11 18:00")` fine, but Safari
 * (iOS + macOS) returns `Invalid Date` for it — which silently breaks every
 * date comparison (programs get treated as "past" and filtered out, so pages
 * render empty on iPhone/Safari).
 *
 * Normalising the space between the date and time to a "T" makes the string a
 * valid ISO-like local datetime that every browser parses identically. Strings
 * that are already ISO (with "T" or "Z") or date-only contain no such space and
 * pass through unchanged.
 *
 * @param {string|number|Date|null|undefined} value
 * @returns {Date} a Date object (Invalid Date for empty/garbage input)
 */
export const safeDate = (value) => {
  if (value === null || value === undefined || value === "") return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  return new Date(String(value).trim().replace(" ", "T"));
};

/**
 * Parses an engine datetime as a *floating local* wall-clock time.
 *
 * The calendar must place an event on the exact date the API states (the
 * program's Melbourne date), not a date shifted by the viewer's timezone. Raw
 * `new Date("…Z")` converts UTC → the viewer's local time, which can move the
 * event to the previous/next day (e.g. an afternoon UTC time rolls over to the
 * next day in AEST). To avoid that, drop any trailing timezone designator
 * (`Z` or `±HH:mm`) and normalise the space separator, so the wall-clock in the
 * string is taken as-is in local time and the calendar day matches the date
 * shown in the listing.
 *
 * For strings already without a timezone (`Z`/offset) this is identical to
 * `safeDate` — so it only ever corrects UTC values, never alters correct ones.
 *
 * @param {string|number|Date|null|undefined} value
 * @returns {Date}
 */
export const localDate = (value) => {
  if (value === null || value === undefined || value === "") return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  return new Date(
    String(value)
      .trim()
      .replace(" ", "T")
      .replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "")
  );
};

/**
 * Builds a `{ start, end }` pair for a react-big-calendar event from the engine
 * start/end datetimes.
 *
 * Both ends are parsed with `localDate` so the days match the dates the API
 * states (no timezone shift). On top of that this fixes a well-known
 * react-big-calendar quirk: a multi-day event whose `end` falls on midnight of
 * the final day (e.g. a program running 17→20 stored as end "2026-06-20 00:00")
 * renders one day short — 17→19 — because that day has zero duration. When the
 * event spans more than one calendar day, the end is extended to the end of its
 * own day so the full span (17→20) shows inclusively in month view.
 *
 * Single-day events are left untouched, so their real end time is preserved and
 * day/week view stays accurate.
 *
 * @param {string|number|Date|null|undefined} startValue
 * @param {string|number|Date|null|undefined} endValue
 * @returns {{ start: Date, end: Date }}
 */
export const calendarRange = (startValue, endValue) => {
  const start = localDate(startValue);
  let end = localDate(endValue);
  const spansMultipleDays =
    !isNaN(start) &&
    !isNaN(end) &&
    (start.getFullYear() !== end.getFullYear() ||
      start.getMonth() !== end.getMonth() ||
      start.getDate() !== end.getDate());
  if (spansMultipleDays) {
    end = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
      23,
      59,
      59,
      999
    );
  }
  return { start, end };
};

/**
 * Sanitises a native `<input type="date">` value.
 *
 * The HTML date input lets the year field hold up to 6 digits (the spec allows
 * years up to 275760), so a user can enter e.g. "123456-05-12" — not a valid
 * filter date. This caps the year at 4 digits: while a shorter year is being
 * typed the value passes through, but once the year exceeds 4 digits the last
 * accepted value is returned so state never holds a 5–6 digit year. Pair it with
 * `max="9999-12-31"` on the input for the picker's own range UI.
 *
 * @param {string} value      the input's current value ("YYYY-MM-DD")
 * @param {string} prevValue  the last accepted value (returned when invalid)
 * @returns {string}
 */
export const sanitizeDateInput = (value, prevValue = "") => {
  if (!value) return value;
  const year = value.split("-")[0];
  if (year.length > 4) return prevValue == null ? "" : prevValue;
  return value;
};

export default safeDate;
